import { useSession } from '@zhiyue/core';
import React from 'react';
import { fundApi } from '@/services/bff/fund.api';
import { fundraisingApi } from '@/services/bff/fundraising.api';
import { hasOAuth2Session } from '@/services/oauth2';
import type {
  ContactDetail,
  ContributionRow,
  LpDetail,
  OpportunityListItem,
} from '@/types/fundraising';
import {
  type Commitment,
  type ContributionPlan,
  createEmptyFundraisingState,
  type FundraisingCommand,
  type FundraisingDomainState,
  fundraisingReducer,
} from './fundraising';

/** 数据源状态：加载时 → 后端可用（成功） → 后端不可用（失败）。 */
export type FundsSource = 'loading' | 'server' | 'error';

interface FundraisingDomainContextValue {
  state: FundraisingDomainState;
  dispatch: React.Dispatch<FundraisingCommand>;
  reset: () => void;
  fundsSource: FundsSource;
  refreshFunds: () => Promise<void>;
}

const FundraisingDomainContext = React.createContext<
  FundraisingDomainContextValue | undefined
>(undefined);

/** 后端 QueryFundraisingDto 限制 pageSize ≤ 100。 */
const PAGE_SIZE = 100;

const fetchAllPages = async <T,>(
  loader: (
    page: number,
    pageSize: number,
  ) => Promise<{ list: T[]; total: number } | undefined>,
): Promise<T[]> => {
  const first = await loader(1, PAGE_SIZE);
  if (!first?.list) return [];
  const total = first.total ?? first.list.length;
  if (first.list.length >= total) return first.list;

  const pages = Math.ceil(total / PAGE_SIZE);
  const rest = await Promise.all(
    Array.from({ length: pages - 1 }, (_, index) =>
      loader(index + 2, PAGE_SIZE),
    ),
  );
  return [...first.list, ...rest.flatMap((page) => page?.list ?? [])];
};

const mapContributionRows = (rows: ContributionRow[]) => {
  const commitments: Record<string, Commitment> = {};
  const contributionPlans: Record<string, ContributionPlan> = {};

  for (const row of rows) {
    const commitmentId = row.commitmentId || `commitment-${row.planId}`;
    if (!commitments[commitmentId]) {
      commitments[commitmentId] = {
        id: commitmentId,
        opportunityId: row.opportunityId || '',
        fundId: row.fundId,
        lpId: row.lpId,
        amount: row.commitmentAmount,
        evidenceStatus: row.evidenceStatus,
        signedAt: row.signedAt || '',
        actualInvestor: row.actualInvestor ?? null,
      };
    }

    const plan = contributionPlans[row.planId] ?? {
      id: row.planId,
      commitmentId,
      fundId: row.fundId,
      lpId: row.lpId,
      owner: row.owner || '',
      installments: [],
    };
    plan.installments = [
      ...plan.installments.filter((item) => item.id !== row.installmentId),
      {
        id: row.installmentId,
        name: row.name,
        dueAmount: row.dueAmount,
        dueAt: row.dueAt,
        paidAmount: row.paidAmount,
        status:
          row.status as ContributionPlan['installments'][number]['status'],
      },
    ];
    contributionPlans[row.planId] = plan;
  }

  return { commitments, contributionPlans };
};

export const FundraisingDomainProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const [state, dispatch] = React.useReducer(
    fundraisingReducer,
    undefined,
    () => createEmptyFundraisingState(),
  );
  const [fundsSource, setFundsSource] = React.useState<FundsSource>('loading');

  /** 从后端拉取全部基金数据，dispatch 进 store。空列表视为成功（租户尚未建基金）。 */
  const loadFunds = React.useCallback(async () => {
    try {
      const list = await fundApi.listFunds({ includeInactive: true });
      dispatch({
        type: 'hydrateFunds',
        payload: Array.isArray(list) ? list : [],
      });
      setFundsSource('server');
    } catch {
      setFundsSource('error');
    }
  }, []);

  /** 并行拉取 LP / 联系人 / 机会 / 出资。CRM 不保留本地 seed，失败保持空态。 */
  const loadCrm = React.useCallback(async () => {
    try {
      const [lpsList, contactsList, oppsList, contribList] =
        await Promise.allSettled([
          fetchAllPages<LpDetail>((page, pageSize) =>
            fundraisingApi.listLps({ page, pageSize }),
          ),
          fetchAllPages<ContactDetail>((page, pageSize) =>
            fundraisingApi.listContacts({ page, pageSize }),
          ),
          fetchAllPages<OpportunityListItem>((page, pageSize) =>
            fundraisingApi.listOpps({ page, pageSize }),
          ),
          fetchAllPages<ContributionRow>((page, pageSize) =>
            fundraisingApi.listContributions({ page, pageSize }),
          ),
        ]);

      const lps =
        lpsList.status === 'fulfilled'
          ? Object.fromEntries(lpsList.value.map((lp) => [lp.id, lp]))
          : {};
      const contacts =
        contactsList.status === 'fulfilled'
          ? Object.fromEntries(contactsList.value.map((c) => [c.id, c]))
          : {};
      const opps =
        oppsList.status === 'fulfilled'
          ? Object.fromEntries(oppsList.value.map((o) => [o.id, o]))
          : {};
      dispatch({
        type: 'hydrateCrm',
        payload: { lps, contacts, opportunities: opps },
      });

      if (contribList.status === 'fulfilled') {
        dispatch({
          type: 'hydrateContributions',
          payload: mapContributionRows(contribList.value),
        });
      } else {
        dispatch({
          type: 'hydrateContributions',
          payload: { commitments: {}, contributionPlans: {} },
        });
      }
    } catch {
      // CRM 拉取失败不影响基金列表；保持当前 source
    }
  }, []);

  const loadFull = React.useCallback(async () => {
    await loadFunds();
    await loadCrm().catch(() => {});
  }, [loadFunds, loadCrm]);

  React.useEffect(() => {
    if (sessionLoading || !hasOAuth2Session() || !currentUser) {
      return;
    }
    void loadFull();
  }, [loadFull, sessionLoading, currentUser]);

  const refreshFunds = React.useCallback(async () => {
    await loadFull();
  }, [loadFull]);

  const reset = React.useCallback(() => dispatch({ type: 'reset' }), []);
  const value = React.useMemo(
    () => ({
      state,
      dispatch,
      reset,
      fundsSource,
      refreshFunds,
    }),
    [state, reset, fundsSource, refreshFunds],
  );

  return (
    <FundraisingDomainContext.Provider value={value}>
      {children}
    </FundraisingDomainContext.Provider>
  );
};

export const useFundraisingDomain = () => {
  const context = React.useContext(FundraisingDomainContext);
  if (!context) {
    throw new Error(
      'useFundraisingDomain must be used inside FundraisingDomainProvider',
    );
  }
  return context;
};
